/** Client-side multi-condition filter tree (Slice 9, decision D6 — pure FE over the client-side row
 * model). An AND/OR group of conditions (one level of nesting max, Affinity parity) evaluated per
 * row via AG-Grid's external-filter hook. Persisted opaquely in the saved-view config as `filterTree`.
 *
 * M2 IOU (D6): the tree schema stays JSON-serializable so, if server-side filtering ever activates,
 * this evaluator can be ported into lists.get_rows. Keep it a pure function of (tree, row, columns).
 */
import { cellNumber, cellText, cellValues } from './crmCells';
import type { CrmGridColumn } from '../../types';

export type FilterOp =
  | 'contains' | 'not_contains' | 'is' | 'is_not'
  | 'is_empty' | 'is_not_empty'
  | 'is_any_of' | 'is_none_of'
  | 'has_any_of' | 'has_all_of' | 'has_none_of'
  | 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'between'
  | 'before' | 'after';

export interface FilterCondition { kind: 'cond'; field: string; op: FilterOp; value?: unknown }
export interface FilterGroup { kind: 'group'; logic: 'and' | 'or'; children: (FilterCondition | FilterGroup)[] }

const REF_TYPES = new Set(['person', 'person_multi', 'organization', 'organization_multi']);
const MULTI_TYPES = new Set(['multi_enum', 'person_multi', 'organization_multi']);
const NUMERIC_TYPES = new Set(['number', 'currency']);
const ENUM_TYPES = new Set(['enum', 'ranked_enum']);

// operators that need no value input (the value box is hidden for these)
export const NO_VALUE_OPS = new Set<FilterOp>(['is_empty', 'is_not_empty']);
// operators whose value is a SET (multi-select / comma list)
export const SET_OPS = new Set<FilterOp>(['is_any_of', 'is_none_of', 'has_any_of', 'has_all_of', 'has_none_of']);

const ALWAYS: FilterOp[] = ['is_empty', 'is_not_empty'];

/** The operators offered for a column's field_type (the panel's op dropdown). */
export function operatorsForType(fieldType: string): FilterOp[] {
  if (NUMERIC_TYPES.has(fieldType)) return ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', ...ALWAYS];
  if (fieldType === 'date') return ['is', 'is_not', 'before', 'after', 'between', ...ALWAYS];
  if (MULTI_TYPES.has(fieldType)) return ['has_any_of', 'has_all_of', 'has_none_of', ...ALWAYS];
  if (ENUM_TYPES.has(fieldType) || REF_TYPES.has(fieldType)) return ['is_any_of', 'is_none_of', ...ALWAYS];
  return ['contains', 'not_contains', 'is', 'is_not', ...ALWAYS]; // text / long_text
}

const norm = (s: unknown) => cellText(s).toLowerCase().trim();
const asSet = (v: unknown): Set<string> => {
  const arr = Array.isArray(v) ? v : String(v ?? '').split(',');
  return new Set(arr.map((x) => norm(x)).filter((x) => x !== ''));
};

function evalCondition(cond: FilterCondition, row: Record<string, unknown>, fieldType?: string): boolean {
  const raw = row[cond.field];
  const op = cond.op;
  // empties (work for every type): a cell is empty when it has no non-blank value
  const parts = cellValues(raw).filter((s) => s !== '');
  const isEmpty = parts.length === 0;
  if (op === 'is_empty') return isEmpty;
  if (op === 'is_not_empty') return !isEmpty;

  // set membership (enum / multi_enum / ref)
  if (SET_OPS.has(op)) {
    const want = asSet(cond.value);
    if (want.size === 0) return true; // no selection → not a constraint
    const have = new Set(parts.map((s) => s.toLowerCase()));
    if (op === 'is_any_of' || op === 'has_any_of') return [...want].some((w) => have.has(w));
    if (op === 'has_all_of') return [...want].every((w) => have.has(w));
    // is_none_of / has_none_of
    return ![...want].some((w) => have.has(w));
  }

  // route comparators by the field's TYPE (not the operator) — `between` and `is`/`is_not` are shared
  // across numeric/date/text, so an op-only route mis-handled date-between (numeric branch → NaN →
  // hid every row) and date-is (raw-string compare vs a yyyy-mm-dd input). When the type is unknown
  // (columns not supplied, e.g. a unit test) fall back to op-inference to preserve prior behavior.
  const mode = fieldType === 'number' || fieldType === 'currency' ? 'number'
    : fieldType === 'date' ? 'date'
      : fieldType ? 'text'
        : NUMERIC_OPS.has(op) ? 'number' : (op === 'before' || op === 'after') ? 'date' : 'text';
  if (mode === 'number') return evalNumeric(op, raw, cond.value);
  if (mode === 'date') return evalDate(op, raw, cond.value);

  // text
  const hay = norm(raw);
  const needle = norm(cond.value);
  if (op === 'contains') return hay.includes(needle);
  if (op === 'not_contains') return !hay.includes(needle);
  if (op === 'is') return hay === needle;
  if (op === 'is_not') return hay !== needle;
  return true;
}

const NUMERIC_OPS = new Set<FilterOp>(['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between']);

function evalNumeric(op: FilterOp, raw: unknown, value: unknown): boolean {
  const n = cellNumber(raw);
  if (n === null) return false;
  const a = Number((value as { a?: unknown })?.a ?? value);
  if (op === 'between') {
    const b = Number((value as { b?: unknown })?.b);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return true; // incomplete range → not a constraint
    const [lo, hi] = a <= b ? [a, b] : [b, a];
    return n >= lo && n <= hi;
  }
  if (!Number.isFinite(a)) return true;
  const cmp: Partial<Record<FilterOp, boolean>> = {
    eq: n === a, neq: n !== a, gt: n > a, gte: n >= a, lt: n < a, lte: n <= a,
  };
  return cmp[op] ?? true;
}

// day-resolution UTC epoch (drops the time) so a full-timestamp cell (e.g. derived last_email) matches
// a yyyy-mm-dd date input; null when unparseable/empty.
function dayMs(x: unknown): number | null {
  const t = cellText(x);
  if (!t) return null;
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function evalDate(op: FilterOp, raw: unknown, value: unknown): boolean {
  const c = dayMs(raw);
  if (op === 'is' || op === 'is_not') {
    const v = dayMs(value);
    if (v === null) return true;             // no/invalid filter value → not a constraint
    if (c === null) return op === 'is_not';  // empty cell: is→false, is_not→true
    return op === 'is' ? c === v : c !== v;
  }
  if (op === 'between') {
    const a = dayMs((value as { a?: unknown })?.a);
    const b = dayMs((value as { b?: unknown })?.b);
    if (a === null || b === null) return true; // incomplete range → not a constraint
    if (c === null) return false;
    const [lo, hi] = a <= b ? [a, b] : [b, a];
    return c >= lo && c <= hi;
  }
  // before / after
  const v = dayMs(value);
  if (v === null) return true;   // no filter value → not a constraint
  if (c === null) return false;  // an empty cell is neither before nor after a date
  return op === 'before' ? c < v : c > v;
}

/** Evaluate the whole tree against one row. An empty group (no children) passes (not a constraint).
 * `columns` supplies each field's type so comparators route correctly (date-between vs numeric-between). */
export function evaluateFilterTree(tree: FilterGroup | null, row: Record<string, unknown>,
                                   columns?: CrmGridColumn[]): boolean {
  const typeByField = columns ? new Map(columns.map((c) => [c.field_key, c.field_type])) : null;
  const evalGroup = (g: FilterGroup): boolean => {
    if (!g || !g.children || g.children.length === 0) return true;
    const results = g.children.map((c) =>
      c.kind === 'group' ? evalGroup(c) : evalCondition(c, row, typeByField?.get(c.field)));
    return g.logic === 'or' ? results.some(Boolean) : results.every(Boolean);
  };
  return tree ? evalGroup(tree) : true;
}

/** Count the leaf conditions in a tree (for the toolbar "Filter (N)" chip). */
export function countConditions(tree: FilterGroup | null): number {
  if (!tree) return 0;
  return tree.children.reduce((n, c) => n + (c.kind === 'group' ? countConditions(c) : 1), 0);
}
